import { throwError as observableThrowError,  Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Usuario, Login, saveFile, Recuperacao, TokenInfo, CadastroUsuarioLote } from '../../index';
import { environment } from '../../../../environments/environment';
import { RestResponse } from '../../model/rest-response';
import { catchError, map } from 'rxjs/operators';
import { AutenticacaoCustom } from '../../model/autenticacaoCustom';

@Injectable()
export class UsuarioService {

    constructor(private http: HttpClient) { }

    private options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

    getUserRequests(organizacaoId : number): Promise<Usuario[]>{

        return this.http.get(environment.backendBaseUrl + '/manager/usuarios/organizacao/' + organizacaoId)
            .toPromise()
            .then(response =>  response as Usuario[])
            .catch(this.handleError);
    }

    exportExcel(usuarioList: Usuario[]) : any {
        return this.http.post(`${environment.backendBaseUrl}/manager/usuarios/exportExcel`, usuarioList, {'responseType': 'blob' as 'blob', observe: 'response'})
            .subscribe(
                res => {
                saveFile(res.body as Blob, "relatorio_cadastro.xls");
             });
    }

    obterDadosUsuarioOrgPorId(id : number, idOrganizacao : number) : Observable<Usuario> {
        return this.http.get<Usuario>(`${environment.backendBaseUrl}/manager/usuarios/${id}/${idOrganizacao}`, this.options).pipe(
            catchError(this.handleErrorObservable));
    }

    visualizarProprioUsuarioOrgPorId(id : number, idOrganizacao : number) : Observable<Usuario> {
        return this.http.get<Usuario>(`${environment.backendBaseUrl}/manager/usuarios/visualizarproprio/${id}/${idOrganizacao}`, this.options).pipe(
            catchError(this.handleErrorObservable));
    }

    obterDadosUsuarioPorToken(token : string) : Observable<Usuario> {
        return this.http.get<Usuario>(environment.backendBaseUrl + '/manager/usuarios/token/' + token, this.options).pipe(
            catchError(this.handleErrorObservable));
    }

    cadastrar(usuario : Usuario) : Observable<any> {
        return this.http.post(environment.backendBaseUrl + `/manager/usuarios/cadastrar/${usuario.organizacaoId}`, usuario).pipe(
            catchError(this.handleErrorObservable));
    }

    ativarUsuarioPerfilOrganizacao(usuario : Usuario) : Observable<any>  {
        return this.http.post(environment.backendBaseUrl + `/manager/usuarios/ativarUsuarioPerfilOrganizacao`, usuario).pipe(
            catchError(this.handleErrorObservable));
    }

    cadastrarNovoUsuarioCorporativo(usuario : Usuario) : Observable<any> {
        return this.http.post(environment.backendBaseUrl + `/manager/usuarios/cadastrar/novousuariocorporativo`, usuario).pipe(
            catchError(this.handleErrorObservable));
    }

    maxUsuariosLote() : Observable<any> {
        return this.http.get(environment.backendBaseUrl + `/manager/usuarios/cadastrar-lote/max-usuarios`).pipe(
            catchError(this.handleErrorObservable));
    }

    cadastrarLote(cadastroUsuarioLote : CadastroUsuarioLote) : Observable<any> {
        return this.http.post(environment.backendBaseUrl + `/manager/usuarios/cadastrar-lote/${cadastroUsuarioLote.idOrganizacao}`, cadastroUsuarioLote).pipe(
            catchError(this.handleErrorObservable));
    }

    reenviarEmail(usuario : Usuario) : Observable<any> {
        return this.http.post<Usuario>(environment.backendBaseUrl + `/manager/usuarios/cadastrar/reenviar/email`, usuario).pipe(
            catchError(this.handleErrorObservable));
    }

    alterarPin(usuario : Usuario) : Observable<any> {
        return this.http.put(environment.backendBaseUrl + `/manager/usuarios/${usuario.id}/atualizapin`, usuario).pipe(
            catchError(this.handleErrorObservable));
    }

    alterarPinPorPalavraChave(usuarioId: number, organizacaoId: number, recuperacao: Recuperacao): Observable<Recuperacao> {
        return this.http.post<Recuperacao>(environment.backendBaseUrl + `/manager/usuarios/${usuarioId}/organizacao/${organizacaoId}/recuperarporpalavrachave`, recuperacao).pipe(
            catchError(this.handleErrorObservable));
    }

    alterarPinPorCodigoChave(usuarioId: number, organizacaoId: number, recuperacao: Recuperacao) : Observable<Recuperacao> {
        return this.http.post<Recuperacao>(environment.backendBaseUrl + `/manager/usuarios/${usuarioId}/organizacao/${organizacaoId}/recuperarporcodigochave`, recuperacao).pipe(
            catchError(this.handleErrorObservable));
    }

    atualizar(usuario : Usuario) : Observable<any> {
        return this.http.post(environment.backendBaseUrl + `/manager/usuarios/atualizar/${usuario.id}/${usuario.organizacaoId}`, usuario).pipe(
            catchError(this.handleErrorObservable));
    }

    atualizarProprioCadastro(usuario : Usuario, idOrganizacao: number) : Observable<any> {
        return this.http.post(environment.backendBaseUrl + `/manager/usuarios/atualizarpropriocadastro/${usuario.id}/${idOrganizacao}`, usuario).pipe(
            catchError(this.handleErrorObservable));
    }

    atualizarPropriaSenha(usuario : Usuario, idOrganizacao: number) : Observable<any> {
        return this.http.post(environment.backendBaseUrl + `/manager/usuarios/atualizarpropriasenha/${usuario.id}/${idOrganizacao}`, usuario).pipe(
            catchError(this.handleErrorObservable));
    }

    solicitarTrocaEmail(usuario : Usuario) : Observable<any> {
        return this.http.post(environment.backendBaseUrl + `/manager/usuarios/requisicao_troca_email/${usuario.id}`, usuario).pipe(
            catchError(this.handleErrorObservable));
    }

    excluir(usuario: Usuario): Promise<void> {
        const url = environment.backendBaseUrl + '/manager/usuarios/excluir/' + usuario.id + '/' + usuario.organizacaoId + '/' + usuario.emailUsuarioAlteracao + '/' + usuario.idUsuarioAlteracao;

        return this.http.delete(url, this.options)
            .toPromise()
            .then(() => null)
            .catch(this.handleError);
    }

    email(idUsuario: number, idOrganizacao: number): Promise<void> {
        const url = environment.backendBaseUrl + `/manager/usuarios/${idUsuario}/organizacao/${idOrganizacao}/enviaemail`;

        return this.http.get(url)
            .toPromise()
            .then(response =>  response as Usuario)
            .catch(this.handleError);
    }

    obterTokenInfo(idUsuario : number, idOrganizacao : number) : Observable<TokenInfo> {
        return this.http.get<TokenInfo>(environment.backendBaseUrl + `/manager/usuarios/${idUsuario}/${idOrganizacao}/obtertokeninfo`).pipe(
            catchError(this.handleErrorObservable));
    }

    obterTokenInfoRedefinicaoToken(codigoAutenticacao: string) : Observable<TokenInfo> {

        AutenticacaoCustom.setAutenticacaoCustom(`${codigoAutenticacao}`);

        return this.http.get<TokenInfo>(environment.backendBaseUrl + `/manager/usuarios/obterTokenInfoRedefinicaoToken`).pipe(
            catchError(this.handleErrorObservable));
    }

    resetTokenUsuarioOrg(idUsuario : number, idOrganizacao : number, resetTokenDto: any) : Observable<any> {
        return this.http.post(environment.backendBaseUrl + `/manager/usuarios/${idUsuario}/${idOrganizacao}/resetToken`, resetTokenDto).pipe(
            map(() => null),
            catchError(this.handleErrorObservable));
    }

    solicitarResetSenha(usuario : Login) : Observable<RestResponse> {
        return this.http.post<RestResponse>(environment.backendBaseUrl + '/manager/usuarios/emailResetSenha', JSON.stringify(usuario), this.options).pipe(
            catchError(this.handleErrorObservable));
    }

    alteraSenha(usuario : Usuario) : Observable<any> {
        return this.http.post(environment.backendBaseUrl + '/manager/usuarios/atualiza/password', usuario).pipe(
            catchError(this.handleErrorObservable));
    }

    enviarEmailRedefinicaoPIN(idUsuario: number, idOrganizacao: number) : Observable<any> {
        const url = environment.backendBaseUrl + `/manager/usuarios/${idUsuario}/organizacao/${idOrganizacao}/enviaemailredefinicao`;

        return this.http.post(url, {}).pipe(
            map(response => {
                return { message: "Sucesso" };
            }),
            catchError(this.handleErrorObservable));
    }

    redefinirPIN(novoPIN: string, tokenOTP: string, codigoAutenticacao: string): Observable<Recuperacao> {
        const url = `${environment.backendBaseUrl}/manager/usuarios/redefinir`;

        const data = {
            novoPIN, tokenOTP
        };

        AutenticacaoCustom.setAutenticacaoCustom(`${codigoAutenticacao}`);

        return this.http.post(url, data).pipe(
            map(response => response as Recuperacao),
            catchError(this.handleErrorObservable));
    }

    enviarEmailRedefinicaoOTP(idUsuario: number, idOrganizacao: number, resetObject: any) : Observable<any> {
        const url = environment.backendBaseUrl + `/manager/usuarios/${idUsuario}/organizacao/${idOrganizacao}/enviaemailredefinicaootp`;
        return this.http.post(url, resetObject).pipe(
            map(response => {
                return { message: "Sucesso" };
            }),
            catchError(this.handleErrorObservable));
    }

    resetTokenOTP(codigoAutenticacao: string): Observable<any> {
        AutenticacaoCustom.setAutenticacaoCustom(`${codigoAutenticacao}`);

        return this.http.post(environment.backendBaseUrl + '/manager/usuarios/resetotp', null).pipe(
            map(res => ""),
            catchError(this.handleErrorObservable));
    }

    confirmarTrocaDeEmail(token: string): Promise<Usuario> {
        return this.http.post(environment.backendBaseUrl + '/manager/usuarios/confirmacao_troca_email', token)
            .toPromise()
            .then(res => res as Usuario)
            .catch(this.handleError);
    }

    registrarLoginRemovido(idUsuario: number, idOrganizacao: number, login: any): Observable<any> {
        return this.http.post(`${environment.backendBaseUrl}/manager/usuarios/registrarLoginRemovido/${idUsuario}/${idOrganizacao}`, login).pipe(
            catchError(this.handleErrorObservable));
    }

    enviarEmailCodigoChave(usuarioId: number, recuperacao: Recuperacao) {
        return this.http.post<void>(environment.backendBaseUrl + `/manager/usuarios/${usuarioId}/enviarEmailCodigoChave`, recuperacao).pipe(
            catchError(this.handleErrorObservable));
    }

    private handleErrorObservable (error: HttpErrorResponse | any) {
        console.error(error.error.message);
        return observableThrowError(error.error || error);
    }

    private handleError(error: any): Promise<any> {
        console.error('OCORREU UM ERRO:', error.error.message);
        return Promise.reject(error.error.message || error);
    }
}
